<template>
  <b-container class="my-orders-page my-4">
    <div v-if="getActiveAccount">
      <div class="page-header mb-3">
        <h1 class="mb-0"><i class="fas fa-receipt mr-2"></i>My Orders</h1>
        <p class="text-muted mb-0">Everything you've bought and sold on the ZCO2 marketplace.</p>
      </div>

      <div class="chart-wrap mb-4">
        <bar-chart v-if="statsChart" :chart-data="statsChart" :options="statsOptions" />
        <div v-else class="chart-loading text-center text-muted py-5">
          <div class="spinner-border" role="status"></div>
        </div>
      </div>

      <b-tabs v-model="activeTab" content-class="mt-3" @input="onTabChange">
        <b-tab title="Bought">
          <div v-if="loading" class="text-center text-muted py-5">Loading…</div>
          <div v-else-if="!orders.length" class="empty-state text-center py-5">
            <i class="fas fa-basket-shopping mb-2"></i>
            <p class="mb-0">You haven't bought any ZCO2 yet.</p>
          </div>
          <div v-else class="order-list">
            <div v-for="o in orders" :key="o.id" class="order-row">
              <div>
                <div class="order-amount">{{ formatNumber(o.zco2Amount) }} ZCO2</div>
                <div class="order-meta">from {{ truncateAddress(o.sellerWallet) }} · {{ formatTime(o.purchasedAt) }}</div>
              </div>
              <div class="order-total">{{ formatNumber(o.paymentAmount) }} {{ o.paymentTokenSymbol }}</div>
            </div>
          </div>
        </b-tab>

        <b-tab title="Sold">
          <div v-if="loading" class="text-center text-muted py-5">Loading…</div>
          <div v-else-if="!orders.length" class="empty-state text-center py-5">
            <i class="fas fa-hand-holding-dollar mb-2"></i>
            <p class="mb-0">You haven't sold any ZCO2 yet.</p>
          </div>
          <div v-else class="order-list">
            <div v-for="o in orders" :key="o.id" class="order-row">
              <div>
                <div class="order-amount">{{ formatNumber(o.zco2Amount) }} ZCO2</div>
                <div class="order-meta">to {{ truncateAddress(o.buyerWallet) }} · {{ formatTime(o.purchasedAt) }}</div>
              </div>
              <div class="order-total">{{ formatNumber(o.paymentAmount) }} {{ o.paymentTokenSymbol }}</div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <div class="d-flex justify-content-center mt-3" v-if="hasMore">
        <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="loadMore">Load more</b-button>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <h1>Connect your wallet</h1>
      <p>You need to connect your Polygon wallet to view your orders.</p>
      <connect-wallet-button class="mt-2" label="Connect"/>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import Order from '../api/Order'
import BarChart from '../components/Chart/BarChart.js'
import ConnectWalletButton from '../components/structure/ConnectWalletButton.vue'

// ============================================================================
// TEMPORARY DEMO DATA — for an in-person presentation only, purely visual.
// These rows are hardcoded here and never touch the backend or the chain.
// To remove: delete this block and the two `mergeDemo*` calls below.
// ============================================================================
const DEMO_ORDERS_BOUGHT = [
  { id: 'demo-o1', zco2Amount: 120, sellerWallet: '0x8f3Cc1B4a9E27d6F52a1C9a0b7De441F2C9a7A3e', paymentAmount: 1500, paymentTokenSymbol: 'EURe', purchasedAt: moment().subtract(2, 'days').toISOString(), isDemo: true },
  { id: 'demo-o2', zco2Amount: 45, sellerWallet: '0x1Ab6De4f9C2a7B3e8F0d5C1a9E6b3D2F7A4c8E1b', paymentAmount: 618.75, paymentTokenSymbol: 'USDC', purchasedAt: moment().subtract(9, 'days').toISOString(), isDemo: true },
  { id: 'demo-o3', zco2Amount: 300, sellerWallet: '0xC4e2A7f1B9d3E6c8A0f4D2b7C1e9A3f6D8b2E5c0', paymentAmount: 3570, paymentTokenSymbol: 'EURe', purchasedAt: moment().subtract(21, 'days').toISOString(), isDemo: true }
]
const DEMO_ORDERS_SOLD = [
  { id: 'demo-o4', zco2Amount: 80, buyerWallet: '0x7Bd1F4a8C2e6D9b3A0f7C5e1B4d8A2f6C9e3D0b7', paymentAmount: 1136, paymentTokenSymbol: 'USDC', purchasedAt: moment().subtract(4, 'days').toISOString(), isDemo: true },
  { id: 'demo-o5', zco2Amount: 500, buyerWallet: '0x2Fa9C6d3B7e1A4f8D0c2E5b9A3f7C1e4D8b6A0f2', paymentAmount: 5950, paymentTokenSymbol: 'EURe', purchasedAt: moment().subtract(14, 'days').toISOString(), isDemo: true }
]
const DEMO_STATS = [
  { month: moment().subtract(3, 'months').format('YYYY-MM'), bought: 180, sold: 210 },
  { month: moment().subtract(2, 'months').format('YYYY-MM'), bought: 260, sold: 340 },
  { month: moment().subtract(1, 'months').format('YYYY-MM'), bought: 165, sold: 580 },
  { month: moment().format('YYYY-MM'), bought: 465, sold: 80 }
]

export default {
  name: 'MyOrders',

  components: { BarChart, ConnectWalletButton },

  data() {
    return {
      activeTab: 0,
      orders: [],
      loading: false,
      page: 1,
      pageSize: 20,
      hasMore: false,
      stats: []
    }
  },

  computed: {
    ...mapGetters('accounts', ['getActiveAccount']),
    orderType() {
      return this.activeTab === 0 ? 'bought' : 'sold'
    },
    chartColors() {
      const styles = getComputedStyle(document.documentElement)
      return {
        text: (styles.getPropertyValue('--text-main') || '#333').trim(),
        muted: (styles.getPropertyValue('--text-muted') || '#888').trim(),
        grid: (styles.getPropertyValue('--border-subtle') || 'rgba(0,0,0,0.08)').trim()
      }
    },
    statsChart() {
      if (!this.stats.length) return null
      return {
        labels: this.stats.map((s) => moment(s.month, 'YYYY-MM').format('MMM YYYY')),
        datasets: [
          {
            label: 'Bought (ZCO2)',
            data: this.stats.map((s) => s.bought),
            backgroundColor: '#4f8c4a',
            borderRadius: 4,
            maxBarThickness: 34
          },
          {
            label: 'Sold (ZCO2)',
            data: this.stats.map((s) => s.sold),
            backgroundColor: '#c9a227',
            borderRadius: 4,
            maxBarThickness: 34
          }
        ]
      }
    },
    statsOptions() {
      const c = this.chartColors
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, labels: { fontColor: c.text } },
        scales: {
          xAxes: [{ gridLines: { color: c.grid, drawBorder: false }, ticks: { fontColor: c.muted } }],
          yAxes: [{
            gridLines: { color: c.grid, drawBorder: false },
            ticks: { fontColor: c.muted, beginAtZero: true, callback: (label) => new Intl.NumberFormat('de-DE').format(label) }
          }]
        }
      }
    }
  },

  watch: {
    getActiveAccount(newVal) {
      if (newVal) this.init()
    }
  },

  created() {
    if (this.getActiveAccount) this.init()
  },

  methods: {
    init() {
      this.loadOrders()
      this.loadStats()
    },
    onTabChange() {
      this.loadOrders()
    },
    loadOrders() {
      this.loading = true
      this.page = 1
      Order.getOrders(this.getActiveAccount, this.orderType, 1, this.pageSize).then((res) => {
        this.orders = this.mergeDemoOrders(res.data)
        this.hasMore = res.meta ? res.meta.hasMore : false
      }).catch(console.error).finally(() => { this.loading = false })
    },
    // TEMPORARY — see DEMO_ORDERS_* above. Prepended on the first page only.
    mergeDemoOrders(realOrders) {
      const demo = this.orderType === 'bought' ? DEMO_ORDERS_BOUGHT : DEMO_ORDERS_SOLD
      return demo.concat(realOrders)
    },
    loadMore() {
      const nextPage = this.page + 1
      Order.getOrders(this.getActiveAccount, this.orderType, nextPage, this.pageSize).then((res) => {
        this.orders = this.orders.concat(res.data)
        this.page = nextPage
        this.hasMore = res.meta ? res.meta.hasMore : false
      }).catch(console.error)
    },
    loadStats() {
      Order.getMyStats(this.getActiveAccount).then((res) => {
        this.stats = this.mergeDemoStats(res.data)
      }).catch((e) => {
        console.error(e)
        // TEMPORARY — still show the demo months even if the real stats
        // call fails, so the chart isn't left empty during the demo.
        this.stats = this.mergeDemoStats([])
      })
    },
    // TEMPORARY — see DEMO_STATS above. Sums demo + real volume per month so
    // the chart has a believable shape instead of a near-empty new account.
    mergeDemoStats(realStats) {
      const byMonth = {}
      for (const s of DEMO_STATS) byMonth[s.month] = { month: s.month, bought: s.bought, sold: s.sold }
      for (const s of realStats) {
        const existing = byMonth[s.month] || { month: s.month, bought: 0, sold: 0 }
        byMonth[s.month] = { month: s.month, bought: existing.bought + (s.bought || 0), sold: existing.sold + (s.sold || 0) }
      }
      return Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month))
    },
    truncateAddress(addr) {
      if (!addr) return ''
      return addr.substring(0, 7) + '...' + addr.substring(addr.length - 5)
    },
    formatNumber(n) {
      const num = parseFloat(n) || 0
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(num)
    },
    formatTime(date) {
      return moment(date).fromNow()
    }
  }
}
</script>

<style scoped lang="scss">
.chart-wrap {
  position: relative;
  height: 280px;
  width: 100%;
  overflow: hidden;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 16px;

  // vue-chartjs wraps its <canvas> in a plain, unstyled <div> (no explicit
  // height) for Chart.js's own resize-monitor to attach to. Without an
  // explicit height here, that div collapses to the canvas's own (stale)
  // size instead of inheriting this container's height, so the canvas can
  // end up taller than this box and spill out of it — see the same fix
  // applied in Statistics.vue.
  > div {
    height: 100%;
    width: 100%;
  }
}

.order-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
}

.order-amount {
  font-weight: 700;
  color: var(--text-heading);
}

.order-meta {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.order-total {
  font-weight: 700;
  color: var(--accent-green);
}

.empty-state {
  color: var(--text-muted);

  i {
    font-size: 2rem;
  }
}
</style>
